import { PhoneIcon } from '@heroicons/react/24/outline';
import React from 'react';

import { MatterVoiceAgentSummary } from '@/types/enhanced-bland-call';

import { MatterRow } from './MatterRow';

interface MatterListProps {
  matters: MatterVoiceAgentSummary[];
  selectedMatters: Set<string>;
  expandedMatters: Set<string>;
  onToggleSelection: (matterId: string) => void;
  onToggleExpansion: (matterId: string) => void;
  onTaskSelect: (taskId: string) => void;
  totalMatters: number;
  currentPage: number;
  totalPages: number;
  hasFilters: boolean;
}

export const MatterList: React.FC<MatterListProps> = ({
  matters,
  selectedMatters,
  expandedMatters,
  onToggleSelection,
  onToggleExpansion,
  onTaskSelect,
  totalMatters,
  currentPage,
  totalPages,
  hasFilters,
}) => {
  // Empty state
  if (matters.length === 0) {
    return (
      <div className="text-center py-16 bg-white rounded border border-gray-200">
        <PhoneIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
        <h3 className="text-sm font-semibold text-gray-700 mb-2">
          {hasFilters ? 'No matches found' : 'No voice agent activity'}
        </h3>
        <p className="text-xs text-gray-500">
          {hasFilters
            ? 'Try adjusting your search or filters.'
            : 'No matters have voice agent runs at this time.'}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {/* List header */}
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs text-gray-600 font-mono">
          {matters.length} of {totalMatters} matters
          {hasFilters && ' (filtered)'}• Page {currentPage} of {totalPages}
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => {
              const allMatterIds = new Set(matters.map((m) => m.matter_id));
              if (expandedMatters.size === allMatterIds.size) {
                // Collapse all
                allMatterIds.forEach((id) => {
                  if (expandedMatters.has(id)) {
                    onToggleExpansion(id);
                  }
                });
              } else {
                // Expand all
                allMatterIds.forEach((id) => {
                  if (!expandedMatters.has(id)) {
                    onToggleExpansion(id);
                  }
                });
              }
            }}
            className="text-xs text-blue-600 hover:text-blue-800"
          >
            {expandedMatters.size === matters.length
              ? 'Collapse All'
              : 'Expand All'}
          </button>

          <span className="text-xs text-gray-400">•</span>

          <button
            type="button"
            onClick={() => {
              const allMatterIds = new Set(matters.map((m) => m.matter_id));
              if (selectedMatters.size === allMatterIds.size) {
                // Deselect all
                allMatterIds.forEach(onToggleSelection);
              } else {
                // Select all
                allMatterIds.forEach((id) => {
                  if (!selectedMatters.has(id)) {
                    onToggleSelection(id);
                  }
                });
              }
            }}
            className="text-xs text-gray-600 hover:text-gray-800"
          >
            {selectedMatters.size === matters.length
              ? 'Deselect All'
              : 'Select All'}
          </button>
        </div>
      </div>

      {/* Matter rows */}
      <div className="space-y-1">
        {matters.map((matter) => (
          <MatterRow
            key={matter.matter_id}
            matter={matter}
            isSelected={selectedMatters.has(matter.matter_id)}
            isExpanded={expandedMatters.has(matter.matter_id)}
            onToggleSelection={() => onToggleSelection(matter.matter_id)}
            onToggleExpansion={() => onToggleExpansion(matter.matter_id)}
            onTaskSelect={onTaskSelect}
          />
        ))}
      </div>
    </div>
  );
};
